import "@app/App.css";
import { Badge } from "@comps/ui/badge";
import { Button } from "@comps/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import User from "@dto/Users";
import { applyBrightness, DEFAULT_COMPOZZ_COLOR, IsDarkColor } from "@utils/colors";
import { getBadgeStyle, getWsButtonStyle, TW_LangSelect, TW_LangSelectorLabel, TW_LangSelectorSelect, TW_LogoutButton, TW_ProfileOwnerBadge, TW_UserButton, TW_UserButtonPopover, TW_UsernameText, TW_UsersPanelButton, TW_UserText } from "@utils/twClasses";
import { clearAuth } from "@utils/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGUAGES, saveLang } from "src/i18n/i18n";
import { useAppStore, useModelStore, useUserStore } from "src/store";

function UserButton() {

  const { t } = useTranslation();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | undefined>(undefined);
  const users = useAppStore((state) => state.usersByKeys);
  const username = useUserStore((state) => state.username);
  const lang = useUserStore((state) => state.lang);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore((state) => state.user);
  const wsColor = useModelStore((state) => state.wsColor);
  const wsDarkColor = useModelStore((state) => state.wsDarkColor);
  const tenant = useAppStore(state => state.tenant);
  // Use default color when on users page, otherwise use workspace color
  const defaultColor = useMemo(() => tenant?.customColor ?? DEFAULT_COMPOZZ_COLOR, [tenant?.customColor]);
  const isOnUsersPage = location.pathname === '/users';
  const effectiveColor = useMemo(() => isOnUsersPage ? defaultColor : wsColor, [defaultColor, wsColor, isOnUsersPage, tenant?.customColor]);
  const effectiveDarkColor = useMemo(() => isOnUsersPage ? applyBrightness(defaultColor, -25) : wsDarkColor, [defaultColor, wsDarkColor, isOnUsersPage, tenant?.customColor]);
  const isDarkColor = useMemo(() => IsDarkColor(effectiveColor), [effectiveColor]);
  const [userPanelButtonStyle, setUserPanelButtonStyle] = useState(getWsButtonStyle(effectiveColor));
  const [logoutButtonStyle, setLogoutButtonStyle] = useState(getWsButtonStyle(effectiveColor));
  

  const userRole = useModelStore((state) => state.userRole);

  useEffect(() => {
    if (!username) {
      return;
    }
    const user = Array.from(users.values()).find((user) => user.username === username);
    if (user) {
      setCurrentUser(user);
    }
  }, [username, users]);

  useEffect(() => {
    setUserPanelButtonStyle(getWsButtonStyle(effectiveColor));
    setLogoutButtonStyle(getWsButtonStyle(effectiveColor));
  }, [effectiveColor, tenant?.customColor, isOnUsersPage, defaultColor, wsDarkColor]);

  if (!currentUser) {
    return null;
  }


  const onUserPanelButtonMouseEnter = () => setUserPanelButtonStyle(getWsButtonStyle(effectiveDarkColor));
  const onUserPanelButtonMouseLeave = () => setUserPanelButtonStyle(getWsButtonStyle(effectiveColor));
  const onLogoutButtonMouseEnter = () => setLogoutButtonStyle(getWsButtonStyle(effectiveDarkColor));
  const onLogoutButtonMouseLeave = () => setLogoutButtonStyle(getWsButtonStyle(effectiveColor));


  const logout = () => {
    clearAuth();
    setCurrentUser(undefined);
  }

  const changeLang = (newLang: string) => {
    saveLang(newLang, true);
  }

  function LangSelector() {
    return (
      <div className={TW_LangSelect}>
        <p className={TW_LangSelectorLabel}>{t("lang.selector.label")}</p>
        <Select onValueChange={changeLang} value={lang}>
          <SelectTrigger className={TW_LangSelectorSelect}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(LANGUAGES).map(([key, value]) => (
              <SelectItem key={key} value={key}>{value}</SelectItem>
            )
            )}
          </SelectContent>
        </Select>
      </div>
    )
  }

  let userFirstLetter = currentUser.username[0].toLocaleUpperCase();
  if (currentUser.firstname) {
    userFirstLetter = currentUser.firstname[0].toUpperCase();
  }

  let roleName = userRole?.name;
  if (user?.owner) {
    roleName = t("users.table.body.owner");
  }

  const roleBadgeStyle = getBadgeStyle(effectiveColor);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <button className={TW_UserButton} style={{ borderColor: effectiveDarkColor, color: effectiveDarkColor }}>{userFirstLetter}</button>
      </PopoverTrigger>
      <PopoverContent className={TW_UserButtonPopover}>
        <p className={TW_UserText}>{currentUser.firstname} {currentUser.lastname}</p>
        <p className={TW_UsernameText}>{username}</p>
        {roleName &&
          <Badge className={TW_ProfileOwnerBadge}
            style={roleBadgeStyle}>
            {roleName}
          </Badge>
        }
        <LangSelector />
        {user?.owner && <Button className={TW_UsersPanelButton} onClick={() => navigate("/users")} autoFocus={false} onMouseEnter={onUserPanelButtonMouseEnter} onMouseLeave={onUserPanelButtonMouseLeave} style={userPanelButtonStyle}>{t("header.button.users.text")}</Button>}
        <Button className={TW_LogoutButton} onClick={logout} autoFocus={false} onMouseEnter={onLogoutButtonMouseEnter} onMouseLeave={onLogoutButtonMouseLeave} style={logoutButtonStyle}>{t("header.button.logout.text")}</Button>
      </PopoverContent>
    </Popover>
  )
};

export default UserButton;
